import axios from "axios";
import { Constants, CookieService, Utils, MediaOpsService, authansid } from "../../../shared";

export const CreateTaxonomyService = {

    /**
     * Gets all taxonomies for a given placement ID.
     * @function getTaxonomies
     * @param {string} placementId - Placement ID.
     * @returns {[object]}
     * Array of objects, further documentation available at:
     * https://devmediaops-api.annalect.com/dev/docs
    */ 
    getTaxonomy: (placementId) => {
        if (placementId) {
            return axios
                .get(Constants.ClientDataUrl + '/' + Utils.getRouteParam("clientid") + '/taxonomy_values?is_orphan=false', {
                    headers: { ansid: authansid },
                    params: { placement_id: placementId }})
                .then((response) => { return response }, (error) => { return Promise.reject(error) });
        } else {
            return axios
                .get(Constants.ClientDataUrl + '/' + Utils.getRouteParam("clientid") + '/taxonomy_values?is_orphan=true',
                    { headers: { ansid: authansid } })
                .then((response) => { return response }, (error) => { return Promise.reject(error) });
        }
    },

    /**
        * Creates a new taxonomy.
        * @function createTaxonomy
        * @param {object} payload - Taxonomy object.
        * @returns {object}
        * Object, further documentation available at:
        * https://devmediaops-api.annalect.com/dev/docs
    */
    createTaxonomy: (payload, placementId) => {

        if (placementId) {
            return axios.post(Constants.ClientDataUrl + '/' + Utils.getRouteParam("clientid") + '/taxonomy_values?is_orphan=false',
                payload, {
                headers: { ansid: authansid },
                params: { placement_id: placementId }
            })
                .then((response) => { return response },
                    (error) => { return Promise.reject(error) });
        } else {
            return axios.post(Constants.ClientDataUrl + '/' + Utils.getRouteParam("clientid") + '/taxonomy_values?is_orphan=true',
                payload, { 
                    headers: { ansid: authansid } 
                })
                .then((response) => { return response },
                    (error) => { return Promise.reject(error) });
        }

    },

    /**
     * Updates an existing taxonomy.
     * @function updateTaxonomy
     * @param {object} payload - Taxonomy object.
     * @returns {object}
     * Object, further documentation available at:
     * https://devmediaops-api.annalect.com/dev/docs
    */ 
    updateTaxonomy: (payload, placementId) => {
        if (placementId) {
            return axios.put(Constants.ClientDataUrl + '/' + Utils.getRouteParam("clientid") + '/taxonomy_values?is_orphan=false',
                payload, {
                headers: { ansid: authansid },
                params: { placement_id: placementId }
            })
                .then((response) => { return response },
                    (error) => { return Promise.reject(error) });
        } else {
            return axios.put(Constants.ClientDataUrl + '/' + Utils.getRouteParam("clientid") + '/taxonomy_values?is_orphan=true',
                payload, { 
                    headers: { ansid: authansid } 
                })
                .then((response) => { return response },
                    (error) => { return Promise.reject(error) });
        }
    },

    /**
     * Delete an existing taxonomy.
     * @function deleteTaxonomy
     * @param {object} payload - Taxonomy object.
     * @returns {object}
     * Object, further documentation available at:
     * https://devmediaops-api.annalect.com/dev/docs
    */ 
     deleteTaxonomy: (payload) => {
            return axios.put(Constants.ClientDataUrl + '/' + Utils.getRouteParam("clientid") + '/taxonomy_values',
                payload, {
                headers: { ansid: authansid, clientid: Utils.getRouteParam("clientid")}
            })
            .then((response) => { return response },
            (error) => { return Promise.reject(error) });
    },

    /**
     * API details to import taxonomy.
     * @function exportTaxonomy
     * @param {string} placementSynthenticId - Placement ID
     * @param {object} payload - Payload object.
     * @returns {object}
    */ 
     importTaxonomy: (placementSynthenticId, payload) => {
        return axios
            .post(
                Constants.exportTaxonomiesUrl + Utils.getRouteParam("clientid") + "?placement_id=" + (placementSynthenticId ? placementSynthenticId : ""),
                payload,
                {
                    headers: {
                        ansid: authansid,
                        clientid: Utils.getRouteParam("clientid"),
                    }
                }
            )
            .then(
                (response) => {
                    return response && response.data;
                }
            );
    },
    

    /**
     * API details to export taxonomy.
     * @function exportTaxonomy
     * @param {string} taxonomyTypeId - Taxonomy Type ID
     * @param {string} deliveredTaxonomies - Delivered taxonomies List.
     * @param {boolean} isOrphan - Is orphan taxonomy.
     * @returns {object}
    */ 
    exportTaxonomy: (taxonomyTypeId, deliveredTaxonomies, isOrphan) => {
        return axios
            .get(
                Constants.exportTaxonomiesUrl + Utils.getRouteParam("clientid") + "?taxonomytype-id=" + taxonomyTypeId + "&taxonomy-ids=[" + deliveredTaxonomies + "]&is_orphan=" + (isOrphan ? false : true),
                {
                    headers: {
                        ansid: authansid,
                        clientid: Utils.getRouteParam("clientid"),
                        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    }
                }
            )
            .then(
                (response) => {
                    return response && response.data;
                }
            );
    },

    /**
     * The below function is used to apply cascading on taxonomy fields.
     * Updates child field items based on parent field selection and clears dependent form values.
     * Creates a deep copy of fields to avoid mutating the original props.
     * 
     * @function applyCascading
     * @param {object} fieldItem - Selected field item object containing childitems array.
     * @param {array} formFields - Array of form field objects from props (read-only).
     * @param {string} fieldName - Name of the parent field that triggered cascading.
     * @param {array} cascadedFieldIds - Array of IDs that should be cascaded to child fields.
     * @param {object} formDataCopy - Copy of form data object to be updated with cleared values.
     * @param {function} updateFieldsInState - Callback function to update fields in parent component state.
     * @param {object} cascadingMap - Map object to track cascading relationships between fields.
     * @param {array} currentFields - Current fields array from state to be deep copied and modified.
     * @returns {void}
    */ 
    applyCascading: (fieldItem, formFields, fieldName, cascadedFieldIds, formDataCopy, updateFieldsInState, cascadingMap, currentFields) => {
        // Create a deep copy to avoid mutating the original formFields from props
        const updatedFields = structuredClone(currentFields);
        
        fieldItem.childitems.forEach((childItem, childItemIndex) => {
            formFields?.forEach((fieldObj, fieldObjIndex) => {
                if (fieldObj?.name !== fieldName) {
                    const updatedFieldItems = fieldObj?.fielditems
                        .map(fieldItem => (cascadedFieldIds.includes(fieldItem?.id) ? fieldItem : undefined))
                        .filter(Boolean);

                    if (updatedFieldItems.length > 0) {
                        updatedFields[fieldObjIndex]['fielditems'] = updatedFieldItems;
                        if(fieldObj.isDimensionField){
                            formDataCopy[formFields[fieldObjIndex]['name' + "_dimensionFields"]] = ''
                        } else {
                            formDataCopy[formFields[fieldObjIndex]['name']] = ''
                        }
                        cascadingMap[fieldName] = {childField: fieldObj?.name, cascaded : true};
                    }
                }
            })
        });
        updateFieldsInState(updatedFields, formDataCopy)
    },

    /**
     * The below function is used to create taxonomy schema & delimiter list.
     * @function generateTaxonomySchema
     * @param {object} taxonomy - Taxonomy object.
     * @returns {object} - Taxonomy schema & delimiter list object.
    */ 
    generateTaxonomySchema: (taxonomy) => {
        let taxonomySchema = '';
        let taxonomyNameDelimiterList = [];
        const taxonomyFields = taxonomy?.fields.filter(field => !field?.hidden);
        taxonomyFields && taxonomyFields?.forEach((field, fieldIndex) => {
            taxonomyNameDelimiterList.push({name: field?.name, alias: field?.alias,  delimiter: field?.delimiter_value});
            if(fieldIndex !== taxonomyFields.length - 1)
                taxonomySchema = taxonomySchema + field.name + "|";
            else
                taxonomySchema = taxonomySchema + field.name;
        })
        return {taxonomySchema, taxonomyNameDelimiterList};
    },

    /**
     * Fetches all taxonomy data for dropdown.
     * 
     * Sends a GET request to the server to retrieve all taxonomy data associated with the current client ID.
     * The client ID is retrieved from a cookie. The request includes an 'ansid' header, the value of which is also retrieved from a cookie.
     * 
     * @returns {Promise} A Promise that resolves to the response data if the request is successful.
     * If the request fails, the Promise is rejected with an error.
     */
    getAllTaxonomyDataForDropdown: () => {
        return axios.get(
            Constants.ClientDataUrl + '/' + Utils.getRouteParam("clientid") + '/taxonomies',
            { headers: { ansid: CookieService.getCookie('ANsid') } })
            .then((response) => {
                return response?.data;
            }, (error) => {
                return Promise.reject(error);
            });
    }
}
